/**
 * End-of-section screen shown when a SHSAT section finishes. Mirrors the
 * official TestNav frame:
 *
 *   Congratulations, you have finished!
 *   End of ELA – Stand alone items
 *   ✓ All Questions Answered
 *   [ Continue ]  (non-final section)  /  [ Submit Final Answers ]  (last section)
 *
 * Presentational only — reuses the existing end-screen markup/classes
 * (``digitalSHSATModule__endContainer`` etc. from Module/index.css) so it
 * mirrors the current terminal end screen. Used by BOTH the legacy locked
 * flow (Module/index.tsx) and the adaptive flow (AdaptiveModule frames).
 *
 * Behavior lives in the caller: the button just continues the existing
 * flow (advance to the next section, or trigger the existing final-submit
 * for the last section). This component owns NO navigation/submit logic.
 */

import React from 'react';
import { FaCheckCircle } from 'react-icons/fa';

export function SectionEndScreen(props: {
  /** e.g. "End of ELA – Stand alone items" / "End of Math". */
  title: string;
  /** Green check + "All Questions Answered" when true; otherwise the
   * ``notAllAnsweredMessage`` (or a default) is shown. Locked flows enforce
   * no-skip, so this is normally true. */
  allAnswered: boolean;
  /** Button label — "Continue" for a non-final section, "Submit Final
   * Answers" for the last section. */
  buttonLabel: string;
  onButton: () => void;
  notAllAnsweredMessage?: React.ReactNode;
}): JSX.Element {
  const { title, allAnswered, buttonLabel, onButton, notAllAnsweredMessage } = props;
  return (
    <div className="digitalSHSATModule__endContainer">
      <div className="digitalSHSATModule__endHeader">Congratulations, you have finished!</div>
      <div className="digitalSHSATModule__endBodyContainer">
        <div className="digitalSHSATModule__endBodyContent">
          <h2 className="digitalSHSATModule__endBodyTitle">{title}</h2>
          <div className="digitalSHSATModule__endBodyExpl">
            {allAnswered ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <FaCheckCircle style={{ color: '#2e9e5b', fontSize: '1.15em' }} />
                All Questions Answered
              </span>
            ) : (
              notAllAnsweredMessage ?? (
                <>
                  Some questions are unanswered. Use the{' '}
                  <span className="digitalSHSATModule__bold">{buttonLabel}</span> button
                  below when you are ready.
                </>
              )
            )}
          </div>
          <button className="digitalSHSATModule__endBodyButton" onClick={onButton}>
            {buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SectionEndScreen;
